// TopUpModal.jsx (Portal Version)
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Dialog } from "@mui/material";
import { styled } from "@mui/material/styles";
import SuccessModal from "./SuccessModal";
import ErrorModal from "./ErrorModal";
import PhoneInput from "./NumberInput";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  createOrder, 
  getCountries, 
  getTopupProductsAgent,
  getOrder as getOrderStatus 
} from "../../../../services/order_services";
import { toast } from "react-toastify";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  CircularProgress,
  Chip,
  LinearProgress,
  Typography
} from "@mui/material";
import redFocusStyles from "../../../../components/ui/formUI/RedFocusStyles";
import { useTranslation } from "react-i18next";

const ORDER_STATUS = {
  QUEUED: 'queued',
  PROCESSING: 'processing',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
  PENDING: 'pending'
};

const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

const StyledDialog = styled(Dialog)(({ theme }) => {
  const isDark = isDarkModeActive();
  return {
    width: "100%",
    "& .MuiBackdrop-root": {
      backdropFilter: "blur(6px)",
      backgroundColor: "rgba(0, 0, 0, 0.2)",
    },
    "& .MuiPaper-root": {
      width: "500px",
      border: `1px solid ${isDark ? "#444" : "#d32f2f"}`,
      borderRadius: 12,
      padding: theme.spacing(2),
      backgroundColor: isDark ? "#1f2937" : theme.palette.background.paper,
    },
  };
});

const ProcessingModal = styled(Dialog)(({ theme }) => {
  const isDark = isDarkModeActive();
  return {
    "& .MuiBackdrop-root": {
      backdropFilter: "blur(6px)",
      backgroundColor: "rgba(0, 0, 0, 0.2)",
    },
    "& .MuiPaper-root": {
      width: "400px",
      border: `1px solid ${isDark ? "#444" : "#d32f2f"}`,
      borderRadius: 12,
      padding: theme.spacing(3),
      backgroundColor: isDark ? "#1f2937" : theme.palette.background.paper,
    },
  };
});

const CustomTextField = ({ sx, ...props }) => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  return (
    <TextField
      {...props}
      sx={{
        width: "100%",
        borderRadius: "20px",
        "& .MuiOutlinedInput-root": {
          paddingY: "0 !important",
          height: "46px",
          "& .MuiAutocomplete-input": { padding: "0 !important" },
          "&.Mui-focused fieldset": { borderColor: "#CD0C02 !important" },
          "&:hover fieldset": { borderColor: "#ff6666" },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? "#4b5563" : "#e5e7eb",
          },
        },
        "& .MuiInputLabel-root": {
          color: isDark ? "#9ca3af" : "#6b7280",
          "&.Mui-focused": { color: "#CD0C02 !important" },
        },
        "& .MuiInputBase-input": {
          color: isDark ? "#f3f4f6" : "#1f2937",
        },
        "& .MuiFormHelperText-root": {
          color: isDark ? "#9ca3af" : "#6b7280",
        },
        "& .MuiAutocomplete-popupIndicator.Mui-focused": { color: "#CD0C02" },
        "& .MuiAutocomplete-inputRoot": {
          paddingTop: "0 !important",
          paddingBottom: "0 !important",
        },
        ...sx,
      }}
    />
  );
};

const CustomSelect = ({ sx, children, ...props }) => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  return (
    <Select
      {...props}
      sx={{
        width: "100%",
        borderRadius: "20px",
        "& .MuiOutlinedInput-root": {
          paddingY: "0 !important",
          height: "46px",
          "&.Mui-focused fieldset": { borderColor: "#CD0C02 !important" },
          "&:hover fieldset": { borderColor: "#ff6666" },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? "#4b5563" : "#e5e7eb",
          },
        },
        "& .MuiInputLabel-root": {
          color: isDark ? "#9ca3af" : "#6b7280",
          "&.Mui-focused": { color: "#CD0C02 !important" },
        },
        "& .MuiSelect-select": {
          color: isDark ? "#f3f4f6" : "#1f2937",
        },
        "& .MuiSvgIcon-root": {
          color: isDark ? "#9ca3af" : "#6b7280",
        },
        ...sx,
      }}
    >
      {children}
    </Select>
  );
};

const CustomMenuItem = ({ sx, children, ...props }) => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  return (
    <MenuItem
      {...props}
      sx={{
        color: isDark ? "#f3f4f6" : "#1f2937",
        backgroundColor: isDark ? "#1f2937" : "#ffffff",
        "&:hover": {
          backgroundColor: isDark ? "#374151" : "#f3f4f6",
        },
        "&.Mui-selected": {
          backgroundColor: isDark ? "#CD0C02" : "#CD0C02",
          color: "#ffffff",
          "&:hover": {
            backgroundColor: isDark ? "#b30b02" : "#b30b02",
          },
        },
        ...sx,
      }}
    >
      {children}
    </MenuItem>
  );
};

const getOperatorId = (phoneNumber) => {
  if (!phoneNumber) return 2;
  
  const cleanedNumber = phoneNumber.replace(/\D/g, '');
  const prefix = cleanedNumber.substring(0, 2);
  
  if (['78', '73'].includes(prefix)) return 2; 
  if (['76', '77'].includes(prefix)) return 4;
  if (['74'].includes(prefix)) return 1; 
  if (['79', '72'].includes(prefix)) return 3;
  if (['70', '71'].includes(prefix)) return 5; 
  
  return 2;
};

const popularAmounts = [50, 100, 150, 250, 500, 1000];

export default function TopUpModal({ open, onClose, onSuccess }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState("");
  const [phone, setPhone] = useState("");
  const [validationError, setValidationError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isTransferSuccess, setIsTransferSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [phoneErrorMessage, setPhoneErrorMessage] = useState("");
  const [isDark, setIsDark] = useState(false);

  // Order status polling states
  const [orderStatus, setOrderStatus] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pollingProgress, setPollingProgress] = useState(0);
  const pollingRef = useRef(null);
  const pollCountRef = useRef(0);
  const maxPolls = 60; // 3 minutes maximum (60 * 3 seconds)

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPopularAmount, setSelectedPopularAmount] = useState(null);
  const [customProduct, setCustomProduct] = useState(null);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await getCountries();
        setCountries(response?.data || []);
        
        const afgCountry = response?.data?.find(c => c.countryCode === "AF");
        if (afgCountry) {
          setSelectedCountry(afgCountry);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
        toast.error(t('topUpModal.errors.loadCountries') || "Failed to load countries");
      }
    };
    
    if (open) {
      fetchCountries();
    }
  }, [open, t]);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!selectedCountry?.id) return;
      
      try {
        setLoadingProducts(true);
        const response = await getTopupProductsAgent({ 
          countryId: selectedCountry.id 
        });
        
        const productsData = response?.data || [];
        setProducts(productsData);
        
        const customProd = productsData.find(p => 
          p.basePrice === 0 && 
          (p.productName?.toLowerCase().includes('custom') || 
           p.productName?.toLowerCase().includes('topup'))
        );
        setCustomProduct(customProd || null);
        
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error(t('topUpModal.errors.loadProducts') || "Failed to load products");
      } finally {
        setLoadingProducts(false);
      }
    };
    
    fetchProducts();
  }, [selectedCountry, t]);

  const filteredProducts = React.useMemo(() => {
    return products
      .filter(product => product.basePrice > 0)
      .map(product => ({
        id: product.id,
        afn: parseFloat(product.basePrice),
        basePrice: parseFloat(product.basePrice),
        productName: product.productName?.en || product.productName,
        product: product,
      }))
      .sort((a, b) => a.afn - b.afn);
  }, [products]);

  // Start polling for order status
  const startPollingOrderStatus = useCallback(async (orderId) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    pollCountRef.current = 0;
    setPollingProgress(0);
    setIsProcessing(true);

    pollingRef.current = setInterval(async () => {
      try {
        pollCountRef.current++;
        const progress = Math.min((pollCountRef.current / maxPolls) * 100, 100);
        setPollingProgress(progress);

        const statusResponse = await getOrderStatus(orderId);
        const currentStatus = statusResponse.data?.status;
        
        console.log(`Poll ${pollCountRef.current}: Order status:`, currentStatus);
        
        setOrderStatus(currentStatus);
        setOrderDetails(prev => ({
          ...prev,
          ...statusResponse.data
        }));

        if (currentStatus === ORDER_STATUS.SUCCEEDED) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setIsProcessing(false);
          
          // Show success modal
          toast.success(t('topUpModal.success.message') || "Recharge Successfully done!");
          setIsTransferSuccess(true);
          
          // Invalidate queries
          queryClient.invalidateQueries({
            queryKey: ["rechargeLogs"],
            exact: false
          });
          
        } else if (currentStatus === ORDER_STATUS.FAILED) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setIsProcessing(false);
          
          // Show error modal
          const errorMsg = statusResponse.data?.error || 
                          statusResponse.data?.message || 
                          t('topUpModal.errors.activationFailed') || 
                          "Failed to activate the product!";
          
          setIsError(true);
          setErrorMessage(errorMsg);
          toast.error(errorMsg);
          
        } else if (pollCountRef.current >= maxPolls) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setIsProcessing(false);
          
          // Timeout - show error
          setOrderStatus(ORDER_STATUS.FAILED);
          setIsError(true);
          setErrorMessage(t('topUpModal.errors.timeout') || "Request timed out. Please check the status later.");
          toast.error(t('topUpModal.errors.timeout') || "Request timed out");
        }
      } catch (error) {
        console.error("Error polling order status:", error);
        if (pollCountRef.current >= maxPolls) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setIsProcessing(false);
          setOrderStatus(ORDER_STATUS.FAILED);
          setIsError(true);
          setErrorMessage(t('topUpModal.errors.pollingFailed') || "Failed to check order status");
        }
      }
    }, 3000);
  }, [t, queryClient]);

  const closeSuccessModal = () => {
    setIsTransferSuccess(false);
    if (onSuccess) onSuccess(); 
    handleReset(); 
  };
  
  const closeErrorModal = () => {
    setIsError(false);
    setIsProcessing(false);
  };

  const closeProcessingModal = () => {
    // Don't allow closing during active polling
    if (orderStatus !== ORDER_STATUS.QUEUED && orderStatus !== ORDER_STATUS.PROCESSING) {
      setIsProcessing(false);
    }
  };
  
  const { mutate, isPending } = useMutation({
    mutationFn: (payload) => createOrder(payload),
    onSuccess: (response) => {
      console.log("Order created successfully:", response);
      
      const orderData = response?.data || response;
      const orderId = orderData?.id || orderData?.orderId;
      
      if (orderId) {
        // Set initial order details
        setOrderDetails({
          orderId: orderId,
          txnNumber: orderData?.txnNumber || `TXN-${Date.now()}`,
          mobile: phone,
          amountAfn: parseFloat(amount),
          date: new Date().toISOString(),
          status: ORDER_STATUS.QUEUED
        });
        
        // Start polling for status updates
        startPollingOrderStatus(orderId);
      } else {
        // If no order ID returned, treat as immediate success
        toast.success(t('topUpModal.success.message') || "Recharge Successfully done!");
        setIsTransferSuccess(true);
        
        queryClient.invalidateQueries({
          queryKey: ["rechargeLogs"],
          exact: false
        });
      }
    },
    onError: (error) => {
      console.log("Order creation error:", error);
      const message =
        error?.response?.data?.error || 
        error?.response?.data?.message || 
        t('topUpModal.errors.activationFailed') || "Failed to activate the product!";
      
      setIsProcessing(false);
      setIsError(true);
      toast.error(message);
      setErrorMessage(message);
    },
  });
  
  const handlePopularAmountSelect = (amountValue) => {
    setAmount(amountValue.toString());
    setSelectedPopularAmount(amountValue);
    
    const matchingProduct = filteredProducts.find(p => p.afn === amountValue);
    if (matchingProduct) {
      setSelectedProduct(matchingProduct.product);
    } else {
      setSelectedProduct(null);
    }
  };
  
  const handleCustomAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setAmount(value);
    setSelectedPopularAmount(null);
    
    if (value && customProduct) {
      const customAmount = parseFloat(value) || 0;
      setSelectedProduct({
        ...customProduct,
        customAmount: customAmount,
        basePrice: customAmount,
        price: customAmount,
      });
    } else {
      setSelectedProduct(null);
    }
  };
  
  const onTransfer = async () => {
    try {
      // Reset states
      setErrorMessage("");
      setPhoneErrorMessage("");
      setIsError(false);
      setIsTransferSuccess(false);
      
      if (!phone) {
        setPhoneErrorMessage(t('topUpModal.errors.phoneRequired') || "Please Enter the Mobile Number");
        return;
      }
      
      const cleanedPhone = phone.replace(/\s/g, '');
      
      if (isNaN(cleanedPhone)) {
        setPhoneErrorMessage(t('topUpModal.errors.phoneNumber') || "Mobile Number Must be a Number");
        return;
      }
      
      if (!amount) {
        setErrorMessage(t('topUpModal.errors.amountRequired') || "Please Enter the Transfer Amount");
        return;
      }
      
      const amountValue = parseFloat(amount);
      if (isNaN(amountValue)) {
        setErrorMessage(t('topUpModal.errors.amountNumber') || "Amount Must be a Number");
        return;
      }
      
      if (amountValue <= 0) {
        setErrorMessage(t('topUpModal.errors.amountPositive') || "Amount must be greater than 0");
        return;
      }
      
      if (amountValue < 10) {
        setErrorMessage(t('topUpModal.errors.minAmount') || "Minimum amount is 10 AFN");
        return;
      }
      
      if (!selectedCountry?.id) {
        setErrorMessage(t('topUpModal.errors.selectCountry') || "Please select a country");
        return;
      }

      const operatorId = getOperatorId(cleanedPhone);
      
      let productId = null;
      let customAmount = null;
      
      if (selectedProduct) {
        productId = selectedProduct.id;
        customAmount = selectedProduct.customAmount || selectedProduct.basePrice || selectedProduct.price;
      } else if (amount && !isNaN(parseFloat(amount))) {
        if (customProduct) {
          productId = customProduct.id;
          customAmount = parseFloat(amount);
        } else if (products.length > 0) {
          const firstProduct = products.find(p => p.basePrice > 0) || products[0];
          if (firstProduct) {
            productId = firstProduct.id;
            customAmount = parseFloat(amount);
          } else {
            productId = 1; 
            customAmount = parseFloat(amount);
          }
        }
      }
      
      if (!productId || !customAmount || customAmount <= 0) {
        setErrorMessage(t('topUpModal.errors.invalidProduct') || "Invalid product selection");
        return;
      }

      const payload = {
        source: "agent_stock",
        receiver: cleanedPhone, 
        customAmount: customAmount,
        operator: operatorId, 
        currency: "AFN",
        countryId: selectedCountry.id, 
        productId: productId, 
        companyId: "",
        cardCurrency: "USD",
        cardAmount: ""
      };

      console.log("Recharge payload:", payload);
      mutate(payload);
      
    } catch (err) {
      console.log(err);
      setErrorMessage(t('topUpModal.errors.unexpected') || "An unexpected error occurred");
    }
  };
  
  const setPhoneNumber = (phoneNumber) => {
    setPhone(phoneNumber);
    setPhoneErrorMessage("");
  };
  
  const handleReset = () => {
    setAmount("");
    setPhone("");
    setErrorMessage("");
    setPhoneErrorMessage("");
    setSelectedCountry(null);
    setSelectedProduct(null);
    setSelectedPopularAmount(null);
    setProducts([]);
    setCustomProduct(null);
    
    // Reset order status states
    setOrderStatus(null);
    setOrderDetails(null);
    setIsProcessing(false);
    setPollingProgress(0);
    
    // Clear polling interval
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    pollCountRef.current = 0;
  };

  const handleRetry = () => {
    setIsError(false);
    setIsProcessing(false);
    setOrderStatus(null);
    setOrderDetails(null);
    setPollingProgress(0);
    
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    pollCountRef.current = 0;
    
    onTransfer();
  };

  const getStatusMessage = () => {
    switch (orderStatus) {
      case ORDER_STATUS.QUEUED:
        return t('topUpModal.status.queued') || "Order queued, waiting to process...";
      case ORDER_STATUS.PROCESSING:
      case ORDER_STATUS.PENDING:
        return t('topUpModal.status.processing') || "Processing your top-up request...";
      case ORDER_STATUS.SUCCEEDED:
        return t('topUpModal.status.completed') || "Top-up completed successfully!";
      case ORDER_STATUS.FAILED:
        return t('topUpModal.status.failed') || "Top-up failed";
      default:
        return t('topUpModal.status.initializing') || "Initializing...";
    }
  };

  const getStatusColor = () => {
    switch (orderStatus) {
      case ORDER_STATUS.QUEUED:
        return "#FFA500";
      case ORDER_STATUS.PROCESSING:
      case ORDER_STATUS.PENDING:
        return "#CD0C02";
      case ORDER_STATUS.SUCCEEDED:
        return "#28A745";
      case ORDER_STATUS.FAILED:
        return "#DC3545";
      default:
        return "#6C757D";
    }
  };
  
  return (
    <>
      <StyledDialog
        open={open}
        onClose={() => {
          handleReset();
          onClose();
        }}
      >
        <SuccessModal
          open={isTransferSuccess}
          onClose={closeSuccessModal}
          amount={amount}
        />
        <ErrorModal
          open={isError}
          onClose={closeErrorModal}
          title={t('topUpModal.errorTitle') || "Failed To Recharge"}
          error={errorMessage}
          onRetry={handleRetry}
        >
          <div className="w-full">
            <div className="flex items-center justify-between">
              <h3 className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                {t('topUpModal.receiver') || "Receiver"}
              </h3>
              <h3 className={isDark ? 'text-white' : 'text-gray-900'}>{phone}</h3>
            </div>
            <div className="flex items-center justify-between mt-2">
              <h3 className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                {t('topUpModal.amount') || "Amount"}
              </h3>
              <h3 className={isDark ? 'text-white' : 'text-gray-900'}>{amount} AFN</h3>
            </div>
            <div className="flex items-center justify-between mt-2">
              <h3 className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                {t('topUpModal.date') || "Date"}
              </h3>
              <h3 className={isDark ? 'text-white' : 'text-gray-900'}>{new Date().toLocaleDateString()}</h3>
            </div>
          </div>
        </ErrorModal>
        
        {!isProcessing ? (
          <div className="px-5 py-3">
            <h3 className={`my-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('topUpModal.title') || "Enter the Number you want to TopUp"}
            </h3>
            
            <div className="mb-4">
              <FormControl sx={redFocusStyles} fullWidth>
                <InputLabel sx={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                  {t('topUpModal.country') || "Country"}
                </InputLabel>
                <CustomSelect
                  value={selectedCountry?.id || ""}
                  onChange={(e) => {
                    const country = countries.find(c => c.id === e.target.value);
                    setSelectedCountry(country);
                  }}
                  label={t('topUpModal.country') || "Country"}
                  disabled={loadingProducts}
                >
                  {countries.map((country) => (
                    <CustomMenuItem key={country.id} value={country.id}>
                      {country.countryName} ({country.countryCode})
                    </CustomMenuItem>
                  ))}
                </CustomSelect>
              </FormControl>
            </div>

            <div className="flex items-start justify-center flex-col mb-4">
              <PhoneInput getInputValue={setPhoneNumber} />
              {phoneErrorMessage && (
                <p className="text-[14px] text-red-500 dark:text-red-400 my-1">{phoneErrorMessage}</p>
              )}
            </div>
            
            <div className="mb-4">
              <label htmlFor="" className={`my-2 block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('topUpModal.selectAmount') || "Select Amount"}
              </label>
              
              <div className="flex flex-wrap gap-2 mb-3">
                {popularAmounts.map((amountValue) => (
                  <Chip
                    key={amountValue}
                    label={`${amountValue} AFN`}
                    onClick={() => handlePopularAmountSelect(amountValue)}
                    color={selectedPopularAmount === amountValue ? "primary" : "default"}
                    variant={selectedPopularAmount === amountValue ? "filled" : "outlined"}
                    style={{ 
                      margin: 2, 
                      backgroundColor: selectedPopularAmount === amountValue ? "#CD0C02" : undefined, 
                      color: selectedPopularAmount === amountValue ? "#fff" : undefined,
                      borderColor: isDark && selectedPopularAmount !== amountValue ? '#4b5563' : undefined,
                    }}
                  />
                ))}
              </div>
              
              <div>
                <label htmlFor="" className={`my-2 block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('topUpModal.customAmount') || "Or Enter Custom Amount"}
                </label>
                <input
                  placeholder={t('topUpModal.amountPlaceholder') || "Enter Topup Amount"}
                  value={amount}
                  onChange={handleCustomAmountChange}
                  type="text"
                  className={`w-full border rounded-[10px] py-2 px-3 focus:border-red-500 outline-none transition-all duration-300 ${
                    isDark 
                      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-[#D0D5DD] text-gray-900'
                  }`}
                />
              </div>
              
              {errorMessage && (
                <p className="text-[14px] text-red-500 dark:text-red-400 my-1">{errorMessage}</p>
              )}
            </div>
            
            {selectedProduct && (
              <div className={`mb-4 p-2 rounded ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('topUpModal.selectedProduct') || "Selected Product"}: {selectedProduct.productName?.en || selectedProduct.productName}
                </p>
              </div>
            )}
            
            {loadingProducts && (
              <div className="flex justify-center my-2">
                <CircularProgress size={20} sx={{ color: isDark ? '#9ca3af' : '#6b7280' }} />
                <span className={`ml-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('topUpModal.loadingProducts') || "Loading products..."}
                </span>
              </div>
            )}

            <div className="w-full flex items-center justify-center mt-7 gap-3">
              <button
                onClick={() => {
                  handleReset();
                  onClose();
                }}
                className={`py-2 px-6 border rounded-md w-[30%] transition-colors ${
                  isDark 
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                    : 'border-gray-400 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t('common.close') || "Close"}
              </button>
              <button
                onClick={onTransfer}
                disabled={isPending || loadingProducts}
                className={`text-white w-[70%] py-2 px-6 border border-gray-400 rounded-md transition-colors ${
                  isDark 
                    ? 'bg-red-700 hover:bg-red-800 disabled:bg-gray-600' 
                    : 'bg-[#CD0C02] hover:bg-red-600 disabled:bg-gray-400'
                } disabled:cursor-not-allowed`}
              >
                {isPending ? (t('common.processing') || "Processing...") : (t('topUpModal.recharge') || "Recharge")}
              </button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-3">
            <h3 className={`my-3 text-center text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {getStatusMessage()}
            </h3>
            
            <div className="mb-4">
              <LinearProgress 
                variant="determinate" 
                value={pollingProgress} 
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: isDark ? '#374151' : '#E5E7EB',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getStatusColor(),
                    borderRadius: 4,
                  }
                }}
              />
              <Typography 
                variant="caption" 
                sx={{ 
                  color: isDark ? '#9ca3af' : '#6b7280',
                  display: 'block',
                  textAlign: 'center',
                  mt: 1
                }}
              >
                {Math.round(pollingProgress)}%
              </Typography>
            </div>
            
            {orderDetails && (
              <div className={`p-4 rounded-lg mb-4 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <div className="flex justify-between items-center mb-2">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                    {t('topUpModal.receiver') || "Receiver"}
                  </span>
                  <span className={isDark ? 'text-white font-medium' : 'text-gray-900 font-medium'}>
                    {orderDetails.mobile || phone}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                    {t('topUpModal.transactionId') || "Transaction ID"}
                  </span>
                  <span className={isDark ? 'text-white font-medium' : 'text-gray-900 font-medium'}>
                    {orderDetails.txnNumber || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                    {t('topUpModal.amount') || "Amount"}
                  </span>
                  <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {orderDetails.amountAfn || amount} AFN
                  </span>
                </div>
              </div>
            )}
            
            <div className="flex justify-center">
              <CircularProgress 
                size={40} 
                sx={{ 
                  color: getStatusColor()
                }} 
              />
            </div>
          </div>
        )}
      </StyledDialog>
    </>
  );
}